import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import ReactCountryFlag from 'react-country-flag';
import Marquee from 'react-fast-marquee';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Bitcoin from '../../assets/bitcoin.svg';
import LikeOverlay from '../../components/like/LikeOverlay';
import PriceTimeBadge from '../../components/price-viewer/PriceTimeBadge';
import { generalHelpers, uiHelpers } from '../../helpers';
import { holdPlace } from '../../helpers/general.helper';
import { useGeneralErrorHandler } from '../../hooks/useGeneralErrorHandler';
import { useMembership } from '../../hooks/users';
import { quickPriceChangeSpanV2 } from '../../modules/Insight/StockPage';
import { LatestQuote } from '../../py-generated/datalouder/price/types';
import { dailypriceService } from '../../services';
const INDEX_LIST = [
  'HK:^HSI',
  'HK:^HSTECH',
  'US:^DJI',
  'US:^IXIC',
  'US:^GSPC',
  'CRPT:BTCUSDT',
];

const INDEX_REGION_DICT = {
  '^HSI': 'HK',
  '^HSTECH': 'HK',
  '^DJI': 'US',
  '^IXIC': 'US',
  '^GSPC': 'US',
  BTCUSDT: 'CRPT',
};

const IndexBarV2: React.FC = () => {
  const { t } = useTranslation();
  const nagivateV2 = useNavigate();
  const hasMembership = useMembership();
  const handleClick = useCallback(
    async (stockCode: string) => {
      stockCode =
        stockCode === 'BTC'
          ? 'btcusdt'
          : stockCode.replace('^', '').toLowerCase();
      nagivateV2(`/lookup/${stockCode}`);
    },
    [nagivateV2]
  );

  const bitcionLogo = () => {
    return <img src={Bitcoin} style={{ width: '0.625rem' }} />;
  };

  const { data: indexPrices, error } = useQuery(
    ['realtimePrice', ...INDEX_LIST],
    () => dailypriceService.realtimePrice(INDEX_LIST),
    { refetchInterval: 120_000 }
  );
  useGeneralErrorHandler(error);

  return (
    <Marquee pauseOnHover speed={20}>
      {(indexPrices || Array.from({ length: INDEX_LIST.length })).map(
        (index: LatestQuote, i) => (
          <div
            key={i}
            className={classNames('index-column-v2', {
              clickable: !!index,
            })}
            onClick={
              index ? () => handleClick(index.ticker) : generalHelpers.noop
            }
            style={{ paddingLeft: '0.75rem' }}
          >
            <div className="index-price-v2" style={{ paddingLeft: '0.5rem' }}>
              {holdPlace(
                indexPrices !== undefined,
                () => (
                  <div>
                    {index?.ticker === 'BTCUSDT' ? (
                      bitcionLogo()
                    ) : (
                      <ReactCountryFlag
                        style={{ paddingBottom: '0.125rem' }}
                        countryCode={INDEX_REGION_DICT[index.ticker]}
                        svg
                      />
                    )}
                    <LikeOverlay stockCode={index.ticker} placement="bottom">
                      <span
                        className="index-name"
                        style={{ paddingLeft: '0.125rem' }}
                      >
                        {t(index.ticker.replace('^', '').toLowerCase())}
                      </span>
                    </LikeOverlay>
                    {holdPlace(indexPrices !== undefined, () => (
                      <PriceTimeBadge ts={index.ts} />
                    ))}
                  </div>
                ),
                24
              )}
              <div
                className="index-price-part-v2"
                style={{ marginRight: '0.5rem' }}
              >
                {holdPlace(indexPrices !== undefined, () =>
                  index?.ticker !== '^IXIC' || hasMembership
                    ? uiHelpers.formatMoney(index.c)
                    : ''
                )}
              </div>
              {index?.c > index?.p_c ? (
                <div style={{ paddingTop: '0.0625rem' }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="7"
                    height="6"
                    viewBox="0 0 7 6"
                    fill="none"
                  >
                    <path
                      d="M0.947388 5.5H6.05323C6.44258 5.5 6.67914 5.07123 6.46722 4.74102L3.9143 0.729287C3.86985 0.659051 3.80836 0.601198 3.73555 0.56111C3.66273 0.521021 3.58097 0.5 3.49785 0.5C3.41473 0.5 3.33296 0.521021 3.26015 0.56111C3.18733 0.601198 3.12584 0.659051 3.08139 0.729287L0.533401 4.74102C0.485321 4.81536 0.458196 4.90129 0.454888 4.98976C0.451579 5.07823 0.472209 5.16595 0.514602 5.24367C0.556995 5.32139 0.619578 5.38623 0.69575 5.43134C0.771922 5.47646 0.858859 5.50018 0.947388 5.5Z"
                      fill="#008533"
                    />
                  </svg>
                </div>
              ) : (
                <div style={{ paddingTop: '0.125rem' }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="7"
                    height="6"
                    viewBox="0 0 7 6"
                    fill="none"
                  >
                    <path
                      d="M5.60066 0.500001L0.494816 0.500001C0.105471 0.500001 -0.131094 0.928774 0.0808287 1.25898L2.63375 5.27071C2.6782 5.34095 2.73969 5.3988 2.8125 5.43889C2.88532 5.47898 2.96708 5.5 3.0502 5.5C3.13332 5.5 3.21509 5.47898 3.2879 5.43889C3.36072 5.3988 3.42221 5.34095 3.46666 5.27071L6.01465 1.25898C6.06273 1.18464 6.08985 1.09871 6.09316 1.01024C6.09647 0.921771 6.07584 0.83405 6.03345 0.75633C5.99106 0.67861 5.92847 0.613773 5.8523 0.568658C5.77613 0.523543 5.68919 0.499823 5.60066 0.500001Z"
                      fill="#BD2B33"
                    />
                  </svg>
                </div>
              )}

              <div className="index-price-part-v2">
                {holdPlace(indexPrices !== undefined, () =>
                  index?.ticker !== '^IXIC' || hasMembership
                    ? quickPriceChangeSpanV2(index.c, index.p_c)
                    : t('click_to_view_detail')
                )}
              </div>
            </div>
          </div>
        )
      )}
    </Marquee>
  );
};

export default IndexBarV2;
